/**
 * McpKeysSection — manage personal MCP API keys for AI assistant access.
 * Users can create, view (prefix only), and revoke keys.
 * The raw key is shown once at creation time with a copy button.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import * as profileApi from '../../api/user-profile';
import type { McpKeyListItem } from '../../api/user-profile';

export function McpKeysSection(): React.ReactElement {
  const [keys, setKeys] = useState<McpKeyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Load keys
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const result = await profileApi.listMcpKeys();
        if (!cancelled) setKeys(result.data ?? []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load keys');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleCreate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    setCreating(true);
    setError(null);
    try {
      const result = await profileApi.createMcpKey(newKeyName.trim());
      setKeys((prev) => [...prev, { keyId: result.keyId, name: result.name, prefix: result.prefix, createdAt: result.createdAt }]);
      setNewlyCreatedKey(result.key);
      setNewKeyName('');
      setShowCreate(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create key');
    } finally {
      setCreating(false);
    }
  }, [newKeyName]);

  const handleRevoke = useCallback(async (keyId: string) => {
    if (!confirm('Revoke this key? Any AI assistants using it will lose access immediately.')) return;

    try {
      await profileApi.revokeMcpKey(keyId);
      setKeys((prev) => prev.filter((k) => k.keyId !== keyId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
    }
  }, []);

  const handleCopy = useCallback(async () => {
    if (!newlyCreatedKey) return;
    await navigator.clipboard.writeText(newlyCreatedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [newlyCreatedKey]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Create API keys to give AI assistants (like Kiro) access to search and browse your wiki content.
        Keys are scoped to your library access.
      </p>

      {error && (
        <p className="text-sm text-destructive" role="alert">{error}</p>
      )}

      {/* Newly created key banner */}
      {newlyCreatedKey && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 space-y-2" role="alert">
          <p className="text-sm font-medium text-yellow-900">
            Copy your key now — it won't be shown again.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white px-2 py-1.5 rounded border font-mono break-all">
              {newlyCreatedKey}
            </code>
            <Button size="sm" variant="outline" onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={() => setNewlyCreatedKey(null)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Key list */}
      {loading && <p className="text-sm text-muted-foreground">Loading keys...</p>}

      {!loading && keys.length === 0 && !showCreate && (
        <p className="text-sm text-muted-foreground">No keys yet.</p>
      )}

      {!loading && keys.length > 0 && (
        <ul className="space-y-2" role="list" aria-label="MCP API keys">
          {keys.map((key) => (
            <li key={key.keyId} className="flex items-center justify-between rounded-md border px-3 py-2">
              <div>
                <p className="text-sm font-medium">{key.name}</p>
                <p className="text-xs text-muted-foreground">
                  <code className="font-mono">{key.prefix}...</code>
                  {' '}
                  created {new Date(key.createdAt).toLocaleDateString()}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => handleRevoke(key.keyId)}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Create form */}
      {showCreate ? (
        <form onSubmit={handleCreate} className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="mcp-key-name">Key name</Label>
            <Input
              id="mcp-key-name"
              placeholder="e.g. Kiro, My Copilot"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              maxLength={100}
              autoFocus
            />
          </div>
          <Button type="submit" size="sm" disabled={creating || !newKeyName.trim()}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
        </form>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowCreate(true)}
          disabled={keys.length >= 5}
        >
          + Create Key
        </Button>
      )}

      {keys.length >= 5 && (
        <p className="text-xs text-muted-foreground">Maximum of 5 keys reached. Revoke an existing key to create a new one.</p>
      )}
    </div>
  );
}
